import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Chip, CircularProgress, IconButton, Popover, Tooltip, Typography, makeStyles } from "@material-ui/core";
import HistoryIcon from "@material-ui/icons/History";
import ConnectionRecoveryStatus from "../ConnectionRecoveryStatus";
import { connectionNeedsAttention, connectionIsRecovering, showConnectionInAlerts, isConnectionOffline } from "../../utils/connectionRecovery";
import SignalWifiOffIcon from "@material-ui/icons/SignalWifiOff";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import WarningOutlinedIcon from "@material-ui/icons/WarningOutlined";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import useNow from "../../hooks/useNow";
import { formatDateTime, formatElapsed, localTimeZone, toDate } from "../../utils/waitingTime";

const useStyles = makeStyles(theme => ({
  button: { color: theme.palette.text.secondary },
  recovering: { color: theme.palette.primary.main },
  warning: { color: theme.palette.type === "dark" ? "#FBBF24" : "#B45309" },
  popover: { width: 430, maxWidth: "calc(100vw - 24px)", border: `1px solid ${theme.custom.border}`, borderRadius: 16 },
  header: { display: "flex", gap: 12, padding: 20, borderBottom: `1px solid ${theme.custom.border}`, alignItems: "center" },
  title: { fontWeight: 800, fontSize: "1rem" },
  subtitle: { fontSize: ".75rem", color: theme.palette.text.secondary, marginTop: 4 },
  list: { maxHeight: "min(55vh, 400px)", overflowY: "auto" },
  item: { margin: 14, padding: 16, borderRadius: 12, border: "1px solid " + theme.custom.border, background: theme.custom.surface },
  attentionItem: { borderColor: "rgba(245,158,11,.35)", background: "rgba(245,158,11,.06)" },
  row: { display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" },
  name: { fontWeight: 700, overflowWrap: "anywhere" },
  timer: { fontVariantNumeric: "tabular-nums", fontWeight: 800, fontSize: "1.3rem", marginTop: 10 },
  date: { display: "block", fontSize: ".75rem", color: theme.palette.text.secondary, marginTop: 6 },
  footer: { padding: 16, borderTop: `1px solid ${theme.custom.border}`, display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" },
  empty: { textAlign: "center", padding: 28, color: theme.palette.text.secondary, "& svg": { marginBottom: 8 } },
  error: { padding: 16, color: theme.palette.error.main, fontSize: ".8rem" },
}));

const labels = { CONNECTED: "Conectado", DISCONNECTED: "Desconectado", OPENING: "Reconectando", qrcode: "Aguardando QR", PAIRING: "Emparelhando", TIMEOUT: "Sem resposta" };

export function ConnectionStatusTime({ connection }) {
  const offline = connection.status !== "CONNECTED";
  const since = toDate(offline ? connection.disconnectedSince : connection.lastConnectedAt);
  const now = useNow(offline && !!since);
  if (!since) return offline ? <Typography variant="caption" color="textSecondary" component="div">Início da desconexão não registrado</Typography> : null;
  return <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 5, fontVariantNumeric: "tabular-nums" }}>
    {offline ? `Sem conexão há ${connection.disconnectedSinceEstimated ? "≥ " : ""}${formatElapsed(Math.max(0, now - since.getTime()))}` : "Conectado"}<br />
    {offline && connection.disconnectedSinceEstimated ? "Observada desde " : "Desde "}<time dateTime={since.toISOString()} title={`Horário local · ${localTimeZone()}`}>{formatDateTime(since)}</time>
  </Typography>;
}

function AdminConnectionAlerts({ user }) {
  const classes = useStyles();
  const [anchor, setAnchor] = useState(null);
  const [connections, setConnections] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const episodes = useRef(new Set());
  const requestNumber = useRef(0);
  const now = useNow(!!anchor && connections.some(connection => isConnectionOffline(connection) && !!toDate(connection.disconnectedSince)));

  useEffect(() => {
    episodes.current = new Set();
    setConnections([]);
    setLoading(true);
    setError(false);
    return () => toast.dismiss(`whatsapp-offline-${user.id}`);
  }, [user.id, user.companyId]);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      const request = ++requestNumber.current;
      setLoading(true);
      try {
        const { data } = await api.get("/whatsapp/alerts");
        if (!active || request !== requestNumber.current) return;
        const observed = (data.connections || []).filter(showConnectionInAlerts);
        const offline = observed.filter(isConnectionOffline);
        const keys = new Set(offline.map(item => `${item.id}:${item.disconnectedSince || "unknown"}`));
        const newlyOffline = offline.filter(item => !episodes.current.has(`${item.id}:${item.disconnectedSince || "unknown"}`));
        if (newlyOffline.length) {
          const first = newlyOffline[0];
          const notification = <div><strong>{newlyOffline.length > 1 ? `${newlyOffline.length} conexões WhatsApp indisponíveis` : `${first.name}: WhatsApp desconectado`}</strong><div style={{ marginTop: 5 }}>{first.disconnectedSince ? `${first.disconnectedSinceEstimated ? "Observada desde" : "Desde"} ${formatDateTime(first.disconnectedSince)}` : "Horário inicial não registrado"}. Veja o alerta de conexões na barra superior.</div></div>;
          const options = {
              toastId: `whatsapp-offline-${user.id}`,
              autoClose: 10000,
              style: { background: "#FFFBEB", color: "#92400E", border: "1px solid #D97706", borderRadius: 12, boxShadow: "0 8px 24px rgba(15,23,42,.16)" },
              closeButton: ({ closeToast }) => <button onClick={closeToast} aria-label="Fechar aviso" style={{ background: "none", border: 0, color: "#92400E", cursor: "pointer", alignSelf: "flex-start", fontSize: 22, padding: "0 4px", lineHeight: 1 }}>×</button>,
          };
          if (toast.isActive(options.toastId)) {
            toast.update(options.toastId, { ...options, render: notification });
          } else {
            toast.warn(notification, options);
          }
        }
        episodes.current = keys;
        setConnections(observed);
        setError(false);
      } catch (_) {
        if (active && request === requestNumber.current) setError(true);
      } finally {
        if (active && request === requestNumber.current) setLoading(false);
      }
    };
    const socket = openSocket();
    socket.on("connect", refresh);
    let refreshTimer = null;
    const scheduleRefresh = () => {
      if (refreshTimer) return;
      refreshTimer = setTimeout(() => { refreshTimer = null; refresh(); }, 150);
    };
    socket.on("whatsappSession", scheduleRefresh);
    socket.on("whatsapp", scheduleRefresh);
    socket.on("whatsappAlert", scheduleRefresh);
    const visibleRefresh = () => { if (!document.hidden) refresh(); };
    const interval = setInterval(visibleRefresh, 30000);
    window.addEventListener("focus", visibleRefresh);
    document.addEventListener("visibilitychange", visibleRefresh);
    refresh();
    return () => {
      active = false;
      socket.disconnect();
      clearInterval(interval);
      clearTimeout(refreshTimer);
      window.removeEventListener("focus", visibleRefresh);
      document.removeEventListener("visibilitychange", visibleRefresh);
    };
  }, [user.id, user.companyId, revision]);

  const attentionCount = connections.filter(connectionNeedsAttention).length;
  const recoveringCount = connections.filter(connection => !isConnectionOffline(connection) && connectionIsRecovering(connection)).length;
  const summary = [attentionCount ? `${attentionCount} ${attentionCount === 1 ? "conexão precisa" : "conexões precisam"} de atenção` : "", recoveringCount ? `${recoveringCount} ${recoveringCount === 1 ? "conexão recuperando" : "conexões recuperando"} mensagens` : ""].filter(Boolean).join(" · ");
  const label = loading && !connections.length && !error ? "Verificando conexões WhatsApp" : error ? "Não foi possível verificar o WhatsApp" : summary || "Conexões WhatsApp sem alertas";
  return <>
    <Tooltip title={label}>
      <IconButton aria-label={label} aria-haspopup="true" aria-expanded={!!anchor} onClick={event => setAnchor(event.currentTarget)} className={`${classes.button} ${attentionCount || error ? classes.warning : recoveringCount ? classes.recovering : ""}`}>
        <Badge badgeContent={error ? "!" : attentionCount} color="error" max={99}>
          {loading && !connections.length && !error ? <CircularProgress size={22} color="inherit" /> : attentionCount || error ? <SignalWifiOffIcon /> : recoveringCount ? <HistoryIcon /> : <CheckCircleOutlineIcon />}
        </Badge>
      </IconButton>
    </Tooltip>
    <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} classes={{ paper: classes.popover }} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
      <div className={classes.header}>
        {error || attentionCount ? <WarningOutlinedIcon className={classes.warning} /> : recoveringCount ? <HistoryIcon color="primary" /> : loading && !connections.length ? <CircularProgress size={22} /> : <CheckCircleOutlineIcon color="primary" />}
        <div><Typography className={classes.title}>Saúde do WhatsApp</Typography><Typography className={classes.subtitle}>{summary || "Acompanhe suas conexões e recuperações recentes"}</Typography></div>
      </div>
      {error && <div role="alert" className={classes.error}>Não foi possível atualizar o status. Os dados abaixo podem estar desatualizados.</div>}
      {loading && !connections.length && <div className={classes.empty}><CircularProgress size={24} aria-label="Verificando conexões" /></div>}
      {!loading && !error && !connections.length && <div className={classes.empty}><CheckCircleOutlineIcon color="primary" fontSize="large" /><Typography variant="body2">Nenhuma conexão indisponível.</Typography></div>}
      <div className={classes.list}>
        {connections.map(connection => {
          const since = toDate(connection.disconnectedSince);
          const offline = isConnectionOffline(connection);
          return <div key={connection.id} className={classes.item + (connectionNeedsAttention(connection) ? " " + classes.attentionItem : "")}>
            <div className={classes.row}><Typography className={classes.name}>{connection.name}</Typography><Chip size="small" label={labels[connection.status] || connection.status} variant="outlined" /></div>
            {offline ? <>
            <Typography className={classes.timer}>{since ? `${connection.disconnectedSinceEstimated ? "≥ " : ""}${formatElapsed(Math.max(0, now - since.getTime()))}` : "—"} <span style={{ fontSize: ".75rem", fontWeight: 500 }}>sem conexão</span></Typography>
            <span className={classes.date}>{since ? <>{connection.disconnectedSinceEstimated ? "Observada desde " : "Desde "}<time dateTime={since.toISOString()}>{formatDateTime(since)}</time></> : "Início da desconexão não registrado"}</span>
            {connection.disconnectedSinceEstimated && <span className={classes.date}>Desconexão anterior ao monitoramento; este é o tempo mínimo observado.</span>}
            {connection.lastConnectedAt && <span className={classes.date}>Última conexão: {formatDateTime(connection.lastConnectedAt)}</span>}
            </> : <ConnectionStatusTime connection={connection} />}
            <ConnectionRecoveryStatus connection={connection} compact />
          </div>;
        })}
      </div>
      <div className={classes.footer}>
        <Button size="small" onClick={() => setRevision(value => value + 1)}>Atualizar</Button>
        <Button size="small" component={Link} to="/connections" onClick={() => setAnchor(null)} color="primary" variant="contained">Gerenciar conexões</Button>
        <Typography variant="caption" color="textSecondary" style={{ width: "100%" }}>Horário local ({localTimeZone()}) · Conexões de teste não geram alertas.</Typography>
      </div>
    </Popover>
  </>;
}

export default function ConnectionAlerts() {
  const { user } = useContext(AuthContext);
  if (!user?.id || (!user.super && user.profile !== "admin")) return null;
  return <AdminConnectionAlerts user={user} />;
}
